import {
    createSlice,
    createAsyncThunk
} from '@reduxjs/toolkit'
import api from '../../api/api'
import { normalizeStoreName } from '../../utils/storeUtils'

export const add_to_card = createAsyncThunk(
    'card/add_to_card',
    async (info, {
        rejectWithValue,
        fulfillWithValue,
        dispatch
    }) => {
        try {
            const optionId = info.optionId || info.productInfo?.selectedOptionId || '';
            if (info.productInfo?.priceOptions?.length && !optionId) return rejectWithValue({ error: 'Choose a product option on its details page' });
            // If userId is missing OR we are in a specific store context (passed via info.storeName)
            // we treat it as a guest/store-specific cart action.
            if (!info.userId || info.storeName) {
                const storageKey = info.storeName ? `guestCart_${normalizeStoreName(info.storeName)}` : 'guestCart';
                let cart = JSON.parse(localStorage.getItem(storageKey)) || [];
                const existingItem = cart.find(item => item.productId === info.productId && (item.optionId || "") === optionId);
                
                if (existingItem) {
                    existingItem.quantity += info.quantity;
                } else {
                    cart.push({
                        productId: info.productId,
                        optionId,
                        quantity: info.quantity,
                        productInfo: info.productInfo,
                        _id: `guest_${crypto.randomUUID()}`
                    });
                }
                localStorage.setItem(storageKey, JSON.stringify(cart));
                return fulfillWithValue({ message: "Added to cart successfully" });
            }

            const {
                data
            } = await api.post('/home/product/add-to-card', info)
            return fulfillWithValue(data)
        } catch (error) {
            console.log(error.response)
            return rejectWithValue(error.response?.data || { error: 'Unable to update cart. Please retry.' })
        }
    }
)

export const get_cart_products = createAsyncThunk(
    'card/get_cart_products',
    async (params, { // Changed from userId to params object or userId
        rejectWithValue,
        fulfillWithValue
    }) => {
        try {
            // Handle both direct userId string (legacy) and params object
            let userId = params;
            let storeName = null;
            
            if (typeof params === 'object' && params !== null) {
                userId = params.userId;
                storeName = params.storeName;
            }

            // If storeName is present, we force reading from local storage for that store
            if (storeName) {
                const storageKey = `guestCart_${normalizeStoreName(storeName)}`;
                const cart = JSON.parse(localStorage.getItem(storageKey)) || [];
                // ... logic to process cart ...
                let card_product_count = 0;
                let price = 0;
                let card_products = [];
                
                if (cart.length > 0) {
                    // Group products by seller
                    const sellerGroups = {};
                    
                    cart.forEach(item => {
                        card_product_count += item.quantity;
                        // Calculate price (assuming productInfo has price and discount)
                        const productPrice = item.productInfo.price;
                        const discount = item.productInfo.discount || 0;
                        const finalPrice = productPrice - (productPrice * discount) / 100;
                        price += finalPrice * item.quantity;

                        const sellerId = item.productInfo.sellerId;
                        if (!sellerGroups[sellerId]) {
                            sellerGroups[sellerId] = {
                                sellerId: sellerId,
                                shopName: item.productInfo.shopName, // Assuming shopName is in productInfo
                                price: 0,
                                products: []
                            };
                        }
                        sellerGroups[sellerId].products.push(item);
                        sellerGroups[sellerId].price += finalPrice * item.quantity;
                    });

                    card_products = Object.values(sellerGroups);
                }

                return fulfillWithValue({
                    card_products,
                    price,
                    card_product_count,
                    outOfStockProduct: [],
                    buy_product_item: 0
                });
            }

            if (!userId) {
                const cart = JSON.parse(localStorage.getItem('guestCart')) || [];
                let card_product_count = 0;
                let price = 0;
                let card_products = [];
                
                if (cart.length > 0) {
                    // Group products by seller
                    const sellerGroups = {};
                    
                    cart.forEach(item => {
                        card_product_count += item.quantity;
                        // Calculate price (assuming productInfo has price and discount)
                        const productPrice = item.productInfo.price;
                        const discount = item.productInfo.discount || 0;
                        const finalPrice = productPrice - (productPrice * discount) / 100;
                        price += finalPrice * item.quantity;

                        const sellerId = item.productInfo.sellerId;
                        if (!sellerGroups[sellerId]) {
                            sellerGroups[sellerId] = {
                                sellerId: sellerId,
                                shopName: item.productInfo.shopName, // Assuming shopName is in productInfo
                                price: 0,
                                products: []
                            };
                        }
                        sellerGroups[sellerId].products.push(item);
                        sellerGroups[sellerId].price += finalPrice * item.quantity;
                    });

                    card_products = Object.values(sellerGroups);
                }

                return fulfillWithValue({
                    card_products,
                    price,
                    card_product_count,
                    outOfStockProduct: [],
                    buy_product_item: 0
                });
            }

            const {
                data
            } = await api.get(`/home/product/get-card-product/${userId}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Unable to update cart. Please retry.' })
        }
    }
)

export const delete_card_product = createAsyncThunk(
    'card/delete_card_product',
    async (params, {
        rejectWithValue,
        fulfillWithValue,
        dispatch
    }) => {
        try {
            let card_id = params;
            let storeName = null;

            if (typeof params === 'object' && params !== null) {
                card_id = params.card_id;
                storeName = params.storeName;
            }

            if (typeof card_id === 'string' && card_id.startsWith('guest_')) {
                 const storageKey = storeName ? `guestCart_${normalizeStoreName(storeName)}` : 'guestCart';
                 let cart = JSON.parse(localStorage.getItem(storageKey)) || [];
                 cart = cart.filter(item => item._id !== card_id);
                 localStorage.setItem(storageKey, JSON.stringify(cart));
                 return fulfillWithValue({ message: "Removed from cart" });
            }

            const {
                data
            } = await api.delete(`/home/product/delete-card-product/${card_id}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Unable to update cart. Please retry.' })
        }
    }
)


export const quantity_inc = createAsyncThunk(
    'card/quantity_inc',
    async (params, {
        rejectWithValue,
        fulfillWithValue,
        dispatch
    }) => {
        try {
            let card_id = params;
            let storeName = null;

            if (typeof params === 'object' && params !== null) {
                card_id = params.card_id;
                storeName = params.storeName;
            }

            if (typeof card_id === 'string' && card_id.startsWith('guest_')) {
                const storageKey = storeName ? `guestCart_${normalizeStoreName(storeName)}` : 'guestCart';
                let cart = JSON.parse(localStorage.getItem(storageKey)) || [];
                const item = cart.find(item => item._id === card_id);
                if (item) {
                    item.quantity += 1;
                    localStorage.setItem(storageKey, JSON.stringify(cart));
                }
                return fulfillWithValue({ message: "Quantity updated" });
            }

            const {
                data
            } = await api.put(`/home/product/quantity-inc/${card_id}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Unable to update cart. Please retry.' })
        }
    }
)

export const quantity_dec = createAsyncThunk(
    'card/quantity_dec',
    async (params, {
        rejectWithValue,
        fulfillWithValue,
        dispatch
    }) => {
        try {
            let card_id = params;
            let storeName = null;

            if (typeof params === 'object' && params !== null) {
                card_id = params.card_id;
                storeName = params.storeName;
            }

            if (typeof card_id === 'string' && card_id.startsWith('guest_')) {
                const storageKey = storeName ? `guestCart_${normalizeStoreName(storeName)}` : 'guestCart';
                let cart = JSON.parse(localStorage.getItem(storageKey)) || [];
                const item = cart.find(item => item._id === card_id);
                if (item && item.quantity > 1) {
                    item.quantity -= 1;
                    localStorage.setItem(storageKey, JSON.stringify(cart));
                }
                return fulfillWithValue({ message: "Quantity updated" });
            }

            const {
                data
            } = await api.put(`/home/product/quantity-dec/${card_id}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Unable to update cart. Please retry.' })
        }
    }
)

export const add_to_wishlist = createAsyncThunk(
    'wishlist/add_to_wishlist',
    async (info, {
        rejectWithValue,
        fulfillWithValue,
        dispatch
    }) => {
        try {
            if (!info.userId || info.storeName) {
                const storageKey = info.storeName ? `guestWishlist_${normalizeStoreName(info.storeName)}` : 'guestWishlist';
                let wishlist = JSON.parse(localStorage.getItem(storageKey)) || [];
                const exists = wishlist.find(item => item.productId === info.productId);
                if (!exists) {
                    wishlist.push({
                        ...info,
                        _id: `guest_wish_${Date.now()}`
                    });
                    localStorage.setItem(storageKey, JSON.stringify(wishlist));
                }
                return fulfillWithValue({ message: "Added to wishlist" });
            }

            const {
                data
            } = await api.post('/home/product/add-to-wishlist', info)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Unable to update cart. Please retry.' })
        }
    }
)

export const get_wishlist_products = createAsyncThunk(
    'wishlist/get_wishlist_products',
    async (params, {
        rejectWithValue,
        fulfillWithValue
    }) => {
        try {
            let userId = params;
            let storeName = null;
            
            if (typeof params === 'object' && params !== null) {
                userId = params.userId;
                storeName = params.storeName;
            }

            if (storeName) {
                const storageKey = `guestWishlist_${normalizeStoreName(storeName)}`;
                const wishlist = JSON.parse(localStorage.getItem(storageKey)) || [];
                return fulfillWithValue({
                    wishlistCount: wishlist.length,
                    wishlists: wishlist
                });
            }

            if (!userId) {
                const wishlist = JSON.parse(localStorage.getItem('guestWishlist')) || [];
                return fulfillWithValue({
                    wishlistCount: wishlist.length,
                    wishlists: wishlist
                });
            }

            const {
                data
            } = await api.get(`/home/product/get-wishlist-products/${userId}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Unable to update cart. Please retry.' })
        }
    }
)

export const remove_wishlist = createAsyncThunk(
    'wishlist/remove_wishlist',
    async (params, {
        rejectWithValue,
        fulfillWithValue,
        dispatch
    }) => {
        try {
            let wishlistId = params;
            let storeName = null;

            if (typeof params === 'object' && params !== null) {
                wishlistId = params.wishlistId;
                storeName = params.storeName;
            }

            if (typeof wishlistId === 'string' && wishlistId.startsWith('guest_wish_')) {
                const storageKey = storeName ? `guestWishlist_${normalizeStoreName(storeName)}` : 'guestWishlist';
                let wishlist = JSON.parse(localStorage.getItem(storageKey)) || [];
                wishlist = wishlist.filter(item => item._id !== wishlistId);
                localStorage.setItem(storageKey, JSON.stringify(wishlist));
                return fulfillWithValue({ message: "Removed from wishlist", wishlistId });
            }

            const {
                data
            } = await api.delete(`/home/product/delete-wishlist-product/${wishlistId}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Unable to update cart. Please retry.' })
        }
    }
)



export const cardReducer = createSlice({
    name: 'card',
    initialState: {
        card_products: [],
        card_product_count: 0,
        buy_product_item: 0,
        wishlist_count: 0,
        wishlist: [],
        price: 0,
        errorMessage: '',
        successMessage: '',
        outofstock_products: []
    },
    reducers: {
        messageClear: (state, _) => {
            state.errorMessage = ''
            state.successMessage = ''
        },
        reset_count: (state, _) => {
            state.card_product_count = 0
            state.wishlist_count = 0
        }
    },
    extraReducers: {
        [add_to_card.rejected]: (state, {
            payload
        }) => {
            state.errorMessage = payload.error
        },
        [add_to_card.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
            state.card_product_count = state.card_product_count + 1
        },
        [get_cart_products.fulfilled]: (state, {
            payload
        }) => {
            state.card_products = payload.card_products
            state.price = payload.price
            state.card_product_count = payload.card_product_count
            state.outofstock_products = payload.outOfStockProduct
            state.buy_product_item = payload.buy_product_item
        },
        [delete_card_product.fulfilled]: (state, {
            payload, meta
        }) => {
            state.successMessage = payload.message
            const cardId = typeof meta.arg === 'object' ? meta.arg.card_id : meta.arg;
            state.card_products = state.card_products.map(p => {
                const products = p.products.filter(pt => pt._id !== cardId);
                return {
                    ...p,
                    products
                }
            }).filter(p => p.products.length > 0)
        },
        [quantity_inc.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
        },
        [quantity_dec.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
        },
        [add_to_wishlist.rejected]: (state, {
            payload
        }) => {
            state.errorMessage = payload.error
        },
        [add_to_wishlist.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
            state.wishlist_count = state.wishlist_count > 0 ? state.wishlist_count + 1 : 1
        },
        [get_wishlist_products.fulfilled]: (state, {
            payload
        }) => {
            state.wishlist = payload.wishlists
            state.wishlist_count = payload.wishlistCount
        },
        [remove_wishlist.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
            state.wishlist = state.wishlist.filter(p => p._id !== payload.wishlistId)
            state.wishlist_count = state.wishlist_count - 1
        }
    }
})

export const {
    messageClear,
    reset_count
} = cardReducer.actions
export default cardReducer.reducer